import ScrollModal from "@/components/common/scroll-modal";
import type { FieldType } from "@/schemas/digital-files";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Heading,
  Icon,
} from "@elaniintc/goes-design-system";
import dayjs from "dayjs";
import { getFieldTypeInfo, getFormattedFieldValue } from "../../utils";
import type { RegistryType } from "@/schemas/digital-files/registry";
import { Link } from "react-router";
import type { Section } from "../../types";
import { getRemainingTimeStr } from "@/utils/procedures-ui";
import { FileLink } from "@/components/common/file-link";
import type { FileType } from "@/types/common/files";
import { useMemo, useState } from "react";
import type { ProccesedExpedientValues } from "@/types/entities/templates";

const AssociatesList = ({
  associates,
}: {
  associates?: RegistryType["associates"];
}) => {
  const [showAll, setShowAll] = useState(false);

  // Group associates by applicant_id to avoid duplicates from multi-role
  const uniqueAssociates = useMemo(() => {
    if (!associates || associates.length === 0) return [];
    const grouped = new Map<
      string,
      {
        applicant_id: string;
        applicant: (typeof associates)[number]["applicant"];
        is_owner: boolean;
        roles: { id?: string; name: string }[];
      }
    >();
    for (const a of associates) {
      const existing = grouped.get(a.applicant_id);
      if (existing) {
        if (a.role && !existing.roles.some((r) => r.id === a.role?.id)) {
          existing.roles.push(a.role);
        }
        if (a.is_owner) existing.is_owner = true;
      } else {
        const toArray = (r: typeof a.role | typeof a.roles) => {
          if (Array.isArray(r)) return r;
          if (r) return [r];
          return [];
        };
        const baseRoles = toArray(a.role);
        grouped.set(a.applicant_id, {
          applicant_id: a.applicant_id,
          applicant: a.applicant,
          is_owner: !!a.is_owner,
          roles: [
            ...baseRoles,
            ...(a.is_owner ? [{ id: "_owner_", name: "Propietario" }] : []),
          ],
        });
      }
    }
    return Array.from(grouped.values());
  }, [associates]);

  if (uniqueAssociates.length === 0)
    return (
      <p className="text-size-body-sm text-body-quiet leading-2xl border-b border-neutral-quiet py-3">
        Sin personas asociadas
      </p>
    );

  const displayedAssociates = showAll
    ? uniqueAssociates
    : uniqueAssociates.slice(0, 3);
  const hasMore = uniqueAssociates.length > 3;
  return (
    <div className="flex flex-col border-b border-neutral-quiet py-2">
      {displayedAssociates.map(
        (associate) =>
          associate.applicant && (
            <div
              key={associate.applicant_id}
              className="flex flex-row gap-3 items-center py-1"
            >
              <Avatar shape="circle" size="xs">
                <AvatarFallback
                  shape="circle"
                  color={associate.is_owner ? "success" : "action"}
                  className={
                    associate.is_owner ? "text-success" : "text-action"
                  }
                >
                  {associate.applicant.first_name
                    ? associate.applicant.first_name.charAt(0).toUpperCase()
                    : "S"}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-body-loud font-semibold text-md leading-2xl">
                  {associate.applicant.first_name}{" "}
                  {associate.applicant.last_name}
                </p>
                <p className="text-body-quiet text-xs leading-xl">
                  {associate.roles.length > 0
                    ? associate.roles.map((r) => r.name).join(", ")
                    : ""}
                </p>
              </div>
            </div>
          ),
      )}
      {hasMore && (
        <Button
          variant="ghost-blue"
          size="xs"
          onClick={() => setShowAll(!showAll)}
          className="mt-2 self-start"
        >
          {showAll ? "Ver menos" : `Ver más`}
          <Icon
            type="far"
            name={showAll ? "chevron-up" : "chevron-down"}
            size="sm"
          />
        </Button>
      )}
    </div>
  );
};

const AssociatedRegistryList = ({
  registries,
  onClose,
}: {
  registries?: RegistryType["related_registries"];
  onClose?: () => void;
}) => {
  const [showAll, setShowAll] = useState(false);

  if (!registries || registries.length === 0)
    return (
      <p className="text-size-body-sm text-body-quiet leading-2xl border-b border-neutral-quiet py-3">
        Sin registros asociados
      </p>
    );

  const displayedAssociates = showAll ? registries : registries.slice(0, 3);
  const hasMore = registries.length > 3;
  ///expedientes/entidad/bd5db91a-0189-4507-92db-6570399b8286/detalle-registro/014f21a7-2fd0-4ebe-af03-9bea0369bad1
  return (
    <div className="flex flex-col py-2 border-b border-neutral-quiet">
      {displayedAssociates.map((associate) => (
        <Link
          to={`/expedientes/entidad/${associate.expedient_base_entity_id}/detalle-registro/${associate.id}`}
          key={associate.id}
          className="w-full flex flex-row gap-3 items-center py-1 hover:bg-neutral-object-accent-quiet rounded-md"
          onClick={() => {
            onClose?.();
          }}
        >
          <Avatar shape="square" size="xs">
            <AvatarFallback shape="square" color="neutral">
              <Icon
                type="fas"
                name="folder"
                size="sm"
                className="text-body-quiet"
              />
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-body-loud font-semibold text-md leading-2xl">
              {associate.name}
            </p>
            <p className="text-body-quiet text-xs leading-xl">
              {`${associate.unique_code} ${
                associate.base_entity_name
                  ? ` | ${associate.base_entity_name}`
                  : ""
              }`}
            </p>
          </div>
        </Link>
      ))}
      {hasMore && (
        <Button
          variant="ghost-blue"
          size="xs"
          onClick={() => setShowAll(!showAll)}
          className="mt-2 self-start"
        >
          {showAll ? "Ver menos" : `Ver más`}
          <Icon
            type="far"
            name={showAll ? "chevron-up" : "chevron-down"}
            size="sm"
          />
        </Button>
      )}
    </div>
  );
};

const FieldDetail = ({
  field,
  valueOption: { options, value },
  expirationDate,
}: {
  field: FieldType;
  valueOption: {
    options: string[];
    value: unknown[]; //string[] | string | number | null | FileType | Array<FileType>;
  };
  expirationDate: string | null;
}) => {
  const info = getFieldTypeInfo(field);
  const remainingTime = useMemo(
    () => (expirationDate ? getRemainingTimeStr(expirationDate) : ""),
    [expirationDate],
  );
  return (
    <div className="flex flex-row gap-3 items-center py-1">
      <Badge
        shape="square"
        size="sm"
        className="size-7 p-0 flex justify-center items-center"
      >
        <Icon type="far" size="sm" name={info.icon} />
      </Badge>
      <div>
        <p className="text-body leading-3xl font-semibold">{field.name}</p>
        {field.configuration?.type === "file" && value ? (
          <div className="flex flex-wrap gap-1">
            {Array.isArray(value) && value.length > 0 ? (
              value.map((file) => (
                <FileLink
                  key={(file as FileType)?.id}
                  file={file as FileType}
                />
              ))
            ) : (
              <p className="text-body-quiet leading-2xl text-size-body-sm">
                Sin asignar
              </p>
            )}
          </div>
        ) : (
          <>
            {options && options.length > 0 && (
              <p className="text-body-quiet leading-2xl text-size-body-sm">
                {options}
              </p>
            )}
            <p
              className={`flex flex-col text-body-quiet leading-2xl text-size-body-sm ${options && options.length > 0 ? "ml-2" : ""}`}
            >
              {getFormattedFieldValue(
                field.configuration?.type,
                value,
                "Sin asignar",
              ).map((val, index) => (
                <span key={`${val}-${index}`}>{val}</span>
              ))}
            </p>
          </>
        )}
        {remainingTime && (
          <p>
            {
              <Badge size="sm" variant="warning">
                <Icon
                  type="far"
                  size="sm"
                  name="clock"
                  className="text-warning-quiet"
                />
                Vence en: {remainingTime}
              </Badge>
            }
          </p>
        )}
      </div>
    </div>
  );
};

interface MoreDetailsModalProps {
  entityId?: string;
  registry: RegistryType;
  processedValues: ProccesedExpedientValues;
  expirations: Record<string, string>;
  sections: Section[];
  isOpen: boolean;
  onClose?: () => void;
}

export const MoreDetailsModal = ({
  entityId,
  registry,
  sections,
  processedValues,
  expirations,
  isOpen,
  onClose,
}: MoreDetailsModalProps) => {
  const sortedSections = useMemo(
    () =>
      [...sections]
        .sort((a, b) => a.order - b.order)
        .map((section) => ({
          ...section,
          fields: [...section.fields].sort((a, b) => a.order - b.order),
        })),
    [sections],
  );
  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={() => onClose?.()}
      modalContentClassName="w-full max-w-[340px] md:max-w-[520px] max-h-[680px] flex flex-col gap-8"
      header={
        <div className="py-3 px-8">
          <div className="flex items-center justify-between w-full">
            <Heading variant="h5">Detalle</Heading>
            <Button
              variant="quiet"
              size="icon"
              aria-label="Cerrar"
              onClick={() => onClose?.()}
              className="px-2 py-1.5 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        </div>
      }
      footer={
        <div className="w-full flex items-center justify-end gap-3 py-4 px-8">
          <Button
            variant="quiet"
            aria-label="Cerrar"
            onClick={() => onClose?.()}
          >
            Cancelar
          </Button>
          <Link to={`/expedientes/entidad/${entityId}/registro/${registry.id}`}>
            <Button aria-label="Editar">Editar</Button>
          </Link>
        </div>
      }
    >
      <div className="px-8">
        {/* title */}
        <div className="flex flex-col gap-4 pb-3">
          <div className="flex flex-col gap-5">
            <div className="flex gap-2">
              <Avatar shape="circle" size="sm">
                <AvatarFallback shape="circle" color="neutral">
                  <Icon
                    type="fas"
                    name="folder"
                    size="sm"
                    className="text-body-quiet"
                  />
                </AvatarFallback>
              </Avatar>
              <div>
                <Heading variant="h6" color="loud">
                  {registry?.name ?? ""}
                </Heading>
                {/* updates */}
                {registry?.updated_at ? (
                  <p className="text-size-body-sm text-body-quiet leading-2xl">
                    Última actualización:{" "}
                    <span className="capitalize">
                      {dayjs(registry.updated_at).format("DD MMM, YYYY")}
                    </span>
                    {(registry.updated_by || registry.created_by) && (
                      <>
                        {" "}
                        por{" "}
                        <span className="text-action">
                          {registry.updated_by
                            ? registry.updated_by.name
                            : registry.created_by?.name}
                        </span>
                      </>
                    )}
                  </p>
                ) : (
                  <p className="text-size-body-xs text-body-quiet leading-2xl">
                    Sin actualizaciones
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* people */}
        <AssociatesList associates={registry.associates} />

        {/* Registries */}
        <AssociatedRegistryList
          registries={registry.related_registries}
          onClose={onClose}
        />

        {/* Inmueble sections */}
        {sortedSections
          .filter(
            //!!!!!!!!! hide "relaciones" section !!!!!!!!!
            (section) => section.title.toLowerCase().trim() !== "relaciones",
          )
          .map((section) => (
            <div
              key={section.id}
              className="border-b border-neutral-quiet py-4"
            >
              <p className="text-body-loud leading-3xl font-semibold">
                {section.title}
              </p>
              {section.fields.length > 0 &&
                section.fields.map((field) => (
                  <FieldDetail
                    key={field.id}
                    field={field}
                    valueOption={processedValues[field.id] ?? {}}
                    expirationDate={expirations[field.id]}
                  />
                ))}
            </div>
          ))}
      </div>
    </ScrollModal>
  );
};
